/**
 * Каркас приложения: тёмный сайдбар для админа, чистый топбар для диспетчера.
 *
 * Диспетчеру сайдбар не показывается намеренно: у него один рабочий экран на
 * восемь часов, и каждый сантиметр ширины отдан карточке и звонку. Разделы
 * админки ему всё равно закрыты сервером — прятать нечего, кроме пустого меню.
 *
 * Активный пункт подсвечивает роутер, а не сравнение строк: вложенные пути
 * («/admin/sources» и «/admin/sources/12») должны считаться одним разделом.
 */

import type { ReactNode } from 'react';
import { Link, Outlet } from '@tanstack/react-router';
import {
  AudioLines,
  BookOpenText,
  Briefcase,
  ChartColumnBig,
  Headset,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Phone,
  PhoneForwarded,
  Plug,
  Rows3,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Tags,
  Upload,
  UserRound,
  Users,
  HandCoins,
} from 'lucide-react';

import { useSession } from './auth-guard.js';
import { logout } from '../shared/api/client.js';

/** Одна строка меню: путь, подпись, иконка, минимальная роль */
interface NavItem {
  to: string;
  title: string;
  icon: ReactNode;
  /** true — пункт только для роли admin (сервер отвечает 403 остальным) */
  adminOnly?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const ICON = 'h-[18px] w-[18px] shrink-0';

/**
 * Порядок групп — порядок рабочего дня админа: сначала «что происходит сейчас»,
 * потом база, по которой звоним, потом итоги, и в конце — настройка системы.
 */
const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Сегодня',
    items: [
      { to: '/admin/overview', title: 'Обзор', icon: <LayoutDashboard className={ICON} /> },
      { to: '/desk', title: 'Пульт', icon: <Headset className={ICON} /> },
      { to: '/admin/queue', title: 'Очередь и раздача', icon: <Rows3 className={ICON} /> },
    ],
  },
  {
    title: 'База',
    items: [
      { to: '/admin/contacts', title: 'Контакты', icon: <Users className={ICON} /> },
      { to: '/admin/upload', title: 'Загрузить номера', icon: <Upload className={ICON} />, adminOnly: true },
      { to: '/admin/sources', title: 'Источники', icon: <BookOpenText className={ICON} /> },
      { to: '/admin/cabinets', title: 'Кабинеты', icon: <Briefcase className={ICON} /> },
    ],
  },
  {
    title: 'Итоги',
    items: [
      { to: '/stats/leads', title: 'Лиды', icon: <Sparkles className={ICON} /> },
      { to: '/stats/reach', title: 'Дозвон', icon: <PhoneForwarded className={ICON} /> },
      { to: '/stats/pay', title: 'ЗП', icon: <HandCoins className={ICON} />, adminOnly: true },
      { to: '/stats/dispatchers', title: 'Отчёт: диспетчеры', icon: <ChartColumnBig className={ICON} /> },
      { to: '/stats/sources', title: 'Отчёт: источники', icon: <ChartColumnBig className={ICON} /> },
    ],
  },
  {
    title: 'Управление',
    items: [
      { to: '/admin/dispatchers', title: 'Диспетчеры', icon: <UserRound className={ICON} /> },
      { to: '/admin/telephony', title: 'Телефония', icon: <Phone className={ICON} /> },
      { to: '/admin/recordings', title: 'Записи', icon: <AudioLines className={ICON} /> },
      { to: '/admin/labels', title: 'Метки', icon: <Tags className={ICON} /> },
      { to: '/admin/rounds', title: 'Круги', icon: <SlidersHorizontal className={ICON} /> },
      { to: '/admin/settings', title: 'Настройки', icon: <Settings className={ICON} /> },
      { to: '/admin/integrations', title: 'Интеграции', icon: <Plug className={ICON} /> },
      { to: '/admin/audit', title: 'Журнал', icon: <ListChecks className={ICON} />, adminOnly: true },
    ],
  },
];

const ROLE_TITLES: Record<string, string> = {
  admin: 'Администратор',
  supervisor: 'Супервайзер',
  dispatcher: 'Диспетчер',
};

export function AppShell(): React.JSX.Element {
  const { user } = useSession();

  // Не вошли — только содержимое (страница входа сама занимает весь экран)
  if (!user) {
    return (
      <div className="min-h-screen bg-canvas text-slate-900">
        <Outlet />
      </div>
    );
  }

  if (user.role === 'dispatcher') {
    return (
      <div className="flex min-h-screen flex-col bg-canvas text-slate-900">
        <TopBar name={user.fullName} role={ROLE_TITLES[user.role] ?? user.role} />
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-slate-900">
      <Sidebar isAdmin={user.role === 'admin'} name={user.fullName} role={ROLE_TITLES[user.role] ?? user.role} />
      <div className="min-h-screen pl-[230px]">
        <Outlet />
      </div>
    </div>
  );
}

/** Топбар диспетчера: кто вошёл и выход; всё рабочее живёт на самом пульте */
function TopBar({ name, role }: { name: string; role: string }): React.JSX.Element {
  return (
    <header className="flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-5">
      <Brand dark={false} />
      <div className="ml-auto flex items-center gap-3">
        <span className="text-sm text-slate-500">{role}</span>
        <span className="text-sm font-semibold text-slate-900">{name}</span>
        <LogoutButton className="text-slate-500 hover:bg-slate-100 hover:text-slate-800" />
      </div>
    </header>
  );
}

function Sidebar({
  isAdmin,
  name,
  role,
}: {
  isAdmin: boolean;
  name: string;
  role: string;
}): React.JSX.Element {
  return (
    <aside className="fixed inset-y-0 left-0 flex w-[230px] flex-col bg-sidebar text-slate-300">
      <div className="flex h-14 items-center px-5">
        <Brand dark />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4 [scrollbar-color:#374151_transparent] [scrollbar-width:thin]">
        {NAV_GROUPS.map((group) => (
          <div key={group.title} className="mt-4 first:mt-1">
            <p className="px-2 pb-1.5 text-[11px] font-semibold tracking-wider text-slate-500 uppercase">
              {group.title}
            </p>
            <ul className="flex flex-col gap-0.5">
              {group.items
                .filter((item) => isAdmin || item.adminOnly !== true)
                .map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="flex h-9 items-center gap-2.5 rounded-lg px-2 text-[13px] font-medium hover:bg-sidebar-hover hover:text-white"
                      activeProps={{ className: 'bg-blue-600 text-white hover:bg-blue-600' }}
                    >
                      {item.icon}
                      {item.title}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="flex items-center gap-2.5 border-t border-white/10 px-4 py-3.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sidebar-hover text-sm font-bold text-white">
          {initials(name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-white">{name}</p>
          <p className="text-[11px] text-slate-500">{role}</p>
        </div>
        <LogoutButton className="text-slate-400 hover:bg-sidebar-hover hover:text-white" />
      </div>
    </aside>
  );
}

/** Имя продукта. Нейтральное намеренно: кабинет живёт на kc.aiimpuls.ru */
function Brand({ dark }: { dark: boolean }): React.JSX.Element {
  return (
    <span className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white">
        <Phone className="h-4 w-4" />
      </span>
      <span className={`text-[15px] font-extrabold tracking-tight ${dark ? 'text-white' : 'text-slate-900'}`}>
        Колл-центр
      </span>
    </span>
  );
}

function LogoutButton({ className }: { className: string }): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label="Выйти"
      title="Выйти"
      onClick={() => {
        void logout().then(() => {
          window.location.assign('/login');
        });
      }}
      className={`flex h-9 w-9 items-center justify-center rounded-lg ${className}`}
    >
      <LogOut className="h-[18px] w-[18px]" />
    </button>
  );
}

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase() || '·';
}
